import React, { useState, useEffect } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { ADJUSTMENT_REASONS } from "../models/inventory.js";

export function StockAdjustSheet({ isOpen, onClose, item, onAdjustSuccess }) {
  const [reason, setReason] = useState("Stock Received");
  const [qtyInput, setQtyInput] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("Stock Received");
      setQtyInput("");
      setNotes("");
      setError("");
    }
  }, [isOpen, item]);

  if (!item) return null;

  const currentStock = Number(item.stock || 0);
  const selectedReasonObj = ADJUSTMENT_REASONS.find((r) => r.id === reason) || ADJUSTMENT_REASONS[0];

  // Derive multiplier based on reason type
  const isDeduction = selectedReasonObj.type === "OUT";
  const numQty = Math.abs(Number(qtyInput || 0));
  const effectiveChange = isDeduction ? -numQty : numQty;
  const newCalculatedStock = currentStock + effectiveChange;

  const handleQuickAdd = (delta) => {
    const current = Number(qtyInput || 0);
    const updated = Math.max(1, current + delta);
    setQtyInput(updated.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!qtyInput || numQty === 0) {
      setError("Please enter a valid non-zero adjustment quantity.");
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      await onAdjustSuccess({
        itemId: item.id,
        itemName: item.itemName || item.name || "Item",
        itemSku: item.gstCode || item.sku || "",
        currentStock,
        adjustmentQty: effectiveChange,
        reason,
        notes
      });
      onClose();
    } catch (err) {
      console.error("Adjustment failed:", err);
      setError(err.message || "Failed to adjust stock.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={`Adjust Stock: ${item.itemName || item.name}`}>
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="form-error" style={{ marginBottom: "1rem" }}>
            {error}
          </div>
        )}

        {/* Current Stock Banner */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          padding: "0.85rem",
          borderRadius: "var(--radius-md)",
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Current Stock</div>
            <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--text-main)" }}>
              {currentStock} <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{item.unit || "pcs"}</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", textTransform: "uppercase" }}>New Balance</div>
            <div style={{
              fontSize: "1.25rem",
              fontWeight: "700",
              color: newCalculatedStock < 0 ? "var(--danger)" : "var(--success)"
            }}>
              {newCalculatedStock} <span style={{ fontSize: "0.85rem" }}>{item.unit || "pcs"}</span>
            </div>
          </div>
        </div>

        {/* Reason Selector */}
        <div className="form-group">
          <label htmlFor="adjust-reason" className="form-label">Adjustment Reason</label>
          <select
            id="adjust-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="form-control"
          >
            {ADJUSTMENT_REASONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* Quantity Input */}
        <Input
          label={`Quantity to ${isDeduction ? "Remove (-)" : "Add (+)"}`}
          id="adjust-qty"
          type="number"
          min="1"
          step="1"
          value={qtyInput}
          onChange={(e) => setQtyInput(e.target.value)}
          placeholder="e.g. 25"
          required
        />

        {/* Quick Delta Chips */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          {[5, 10, 25, 50, 100].map((delta) => (
            <button
              key={delta}
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => handleQuickAdd(delta)}
              style={{ minHeight: "30px", padding: "0.25rem 0.65rem", fontSize: "0.75rem" }}
            >
              +{delta}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setQtyInput("")}
            style={{ minHeight: "30px", padding: "0.25rem 0.65rem", fontSize: "0.75rem", color: "var(--danger)" }}
          >
            Clear
          </button>
        </div>

        {/* Notes */}
        <div className="form-group">
          <label htmlFor="adjust-notes" className="form-label">Audit Notes (Optional)</label>
          <input
            id="adjust-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Received from Supplier Invoice #8841"
            className="form-control"
          />
        </div>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
          <Button
            type="button"
            variant="secondary"
            block
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            block
            loading={submitting}
          >
            Confirm Adjustment
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default StockAdjustSheet;
